import { z } from 'zod';
import { addDays } from './calendar/dates.js';
import { PLANS } from './modules.js';
/**
 * Bestellung, Abrechnung und Zahlung (KON-180 bis KON-182): Der Testmandant bestellt ein Paket und wird damit zum bezahlten Mandanten. Verträge zwischen Backoffice und API.
 * Preise und Fristen stehen hier an einer Stelle, auch die Webseite liest sie von hier (apps/website/src/site.mjs).
 */
export const VAT_PERCENT = 19;
export const grossCents = (netCents) => Math.round((netCents * (100 + VAT_PERCENT)) / 100);
export const netCentsOfGross = (gross) => Math.round((gross * 100) / (100 + VAT_PERCENT));
/**
 * Preismodell ab 17.10.2026: Die Pakete kosten je Benutzer und Monat die unten genannten Bruttopreise (inklusive 19 % Umsatzsteuer), werden aber für zwölf Monate im Voraus
 * abgerechnet. In jedem Paket ist ein Benutzer enthalten, jeder weitere kostet monatlich 5 € brutto und lässt sich im Backoffice unter „Benutzer“ buchen.
 * Alle Preise nach außen stehen brutto. Intern rechnet die Rechnung mit Nettobeträgen, deshalb steht der Zeitraumpreis netto in `PLAN_PRICES_NET_CENTS`.
 */
export const PLAN_PRICES_GROSS_MONTH_CENTS = { basic: 1499, standard: 3999, premium: 5999 };
/** Aufschlag der monatlichen Abrechnung auf den Monatspreis der Jahresabrechnung in Prozent (Entscheidung vom 28.10.2026) */
export const MONTHLY_SURCHARGE_PERCENT = 20;
/**
 * Monatliche Abrechnung (zweites Angebot neben der Jahresabrechnung): Preis je Benutzer und Monat, brutto, ohne Vorauszahlung für zwölf Monate. Er liegt 20 % über dem Monatspreis
 * der Jahresabrechnung, aufgerundet auf volle Cent (Basic 17,99 €, Standard 47,99 €, Premium 71,99 €). Die Webseite zeigt ihn an, die Bestellung und die Rechnungen im Backoffice
 * rechnen noch ausschließlich mit der Jahresabrechnung (siehe `BILLING_PERIOD_MONTHS`).
 */
export const PLAN_PRICES_MONTHLY_GROSS_CENTS = Object.fromEntries(PLANS.map((p) => [p, Math.ceil((PLAN_PRICES_GROSS_MONTH_CENTS[p] * (100 + MONTHLY_SURCHARGE_PERCENT)) / 100)]));
/** Länge eines Abrechnungszeitraums in Monaten (Jahresabrechnung) */
export const BILLING_PERIOD_MONTHS = 12;
/** Preis je Abrechnungszeitraum (zwölf Monate), brutto in Cent */
export const PLAN_PRICES_GROSS_CENTS = Object.fromEntries(PLANS.map((p) => [p, PLAN_PRICES_GROSS_MONTH_CENTS[p] * BILLING_PERIOD_MONTHS]));
/** Preis je Abrechnungszeitraum (zwölf Monate), netto in Cent. Brutto daraus ergibt wieder genau `PLAN_PRICES_GROSS_CENTS`. */
export const PLAN_PRICES_NET_CENTS = Object.fromEntries(PLANS.map((p) => [p, netCentsOfGross(PLAN_PRICES_GROSS_CENTS[p])]));
/** Benutzer, die jedes Paket enthält */
export const INCLUDED_USERS = 1;
/** Jeder weitere Benutzer, je Monat */
export const EXTRA_USER_GROSS_MONTH_CENTS = 500;
export const EXTRA_USER_NET_MONTH_CENTS = netCentsOfGross(EXTRA_USER_GROSS_MONTH_CENTS);
/** Zusatzbenutzer, die sich je Buchung höchstens bestellen lassen */
export const EXTRA_USER_MAX_PER_BOOKING = 50;
/** bank: Sofortüberweisung, nur über die Bestellung auf der Webseite (KON-186). Das Backoffice bietet sie nicht an. */
export const BILLING_METHODS = ['invoice', 'stripe', 'paypal', 'bank'];
export const BILLING_METHOD_LABELS = { invoice: 'Überweisung auf Rechnung', stripe: 'Karte oder SEPA-Lastschrift', paypal: 'PayPal', bank: 'Sofortüberweisung' };
/** Zahlungsarten der Bestellung im Backoffice (Testmandant wird zum Kunden) */
export const BACKOFFICE_METHODS = ['invoice', 'stripe', 'paypal'];
/** Fassungen der Bedingungen, die der Kunde bei der Bestellung bestätigt. Sie stehen mit Zeitstempel an der Bestellung. */
export const BILLING_TERMS_VERSION = 'AGB, Entwurf vom 04.11.2026';
export const BILLING_AVV_VERSION = 'Auftragsverarbeitungsvertrag, Entwurf vom 04.11.2026';
/** Fristen der Abrechnung in Tagen. Zahlungsziel einer Rechnung, Erinnerung und Sperre nach der Fälligkeit. */
export const BILLING_DAYS = { paymentTerms: 14, bankTerms: 7, reminderAfterDue: 7, suspendAfterDue: 21 };
/** pending: wartet auf die Bestätigung des Zahlungsanbieters, active: läuft, cancelling: gekündigt zum Ende der bezahlten Zeit, ended: beendet */
export const BILLING_ORDER_STATUSES = ['pending', 'active', 'cancelling', 'ended'];
export const BILLING_ORDER_STATUS_LABELS = { pending: 'Wartet auf Zahlungsanbieter', active: 'Aktiv', cancelling: 'Gekündigt', ended: 'Beendet' };
export const BILLING_PAYMENT_STATUSES = ['open', 'paid', 'overdue', 'failed', 'cancelled', 'refunded'];
export const BILLING_PAYMENT_STATUS_LABELS = { open: 'Offen', paid: 'Bezahlt', overdue: 'Überfällig', failed: 'Fehlgeschlagen', cancelled: 'Storniert', refunded: 'Erstattet' };
const text = (label, min, max) => z.string().trim().min(min, `${label} fehlt`).max(max, `${label} ist zu lang`).refine((v) => !/[<>]/.test(v), `${label}: Bitte keine Sonderzeichen wie < und >`);
export const billingAddressInput = z.object({
    company: text('Firma', 2, 120),
    name: text('Ansprechpartner', 2, 120),
    street: text('Straße und Hausnummer', 3, 120),
    postalCode: text('Postleitzahl', 3, 10),
    city: text('Ort', 2, 80),
    country: z.string().trim().length(2, 'Bitte den Ländercode angeben, zum Beispiel DE').toUpperCase().default('DE'),
    vatId: z.string().trim().max(20).optional().transform((v) => (v ? v.replace(/\s/g, '').toUpperCase() : undefined)),
    email: z.string().trim().toLowerCase().pipe(z.email('Bitte prüfen Sie die E-Mail-Adresse für die Rechnung')),
});
export const placeOrderInput = z
    .object({
    plan: z.enum(PLANS, 'Bitte ein Paket wählen'),
    paymentMethod: z.enum(BACKOFFICE_METHODS, 'Bitte eine Zahlungsart wählen'),
    billing: billingAddressInput,
    acceptTerms: z.literal(true, { error: 'Bitte bestätigen Sie die AGB' }),
    acceptAvv: z.literal(true, { error: 'Bitte bestätigen Sie den Auftragsverarbeitungsvertrag' }),
})
    .strict();
/** Bestellung über den Link aus der Mail zum Ende der Testphase: wie im Backoffice, aber nur mit Zahlungsarten, die der Anbieter sofort bestätigt (ein gesperrter Zugang wird erst danach frei) */
export const reactivationOrderInput = placeOrderInput.extend({ paymentMethod: z.enum(['stripe', 'paypal'], 'Bitte eine Zahlungsart wählen') });
/** Differenz in Tagen (b minus a), Format JJJJ-MM-TT */
export function daysBetween(a, b) {
    return Math.round((Date.parse(`${b}T00:00:00Z`) - Date.parse(`${a}T00:00:00Z`)) / 86_400_000);
}
/** Datum plus ganze Monate, am Monatsende abgeschnitten (31.01. plus 1 Monat ist der 28. oder 29.02.). Format JJJJ-MM-TT. */
export function addMonthsIso(date, months) {
    const [y, m, d] = date.split('-').map(Number);
    const index = y * 12 + (m - 1) + months;
    const year = Math.floor(index / 12);
    const month = (index % 12) + 1;
    const last = new Date(Date.UTC(year, month, 0)).getUTCDate();
    return `${year}-${String(month).padStart(2, '0')}-${String(Math.min(d, last)).padStart(2, '0')}`;
}
// ---------------------------------------------------------------------------
// Herunterstufen
// ---------------------------------------------------------------------------
export const downgradeInput = z
    .object({
    plan: z.enum(PLANS, 'Bitte ein Paket wählen'),
    /** Die ausdrückliche Bestätigung nach der Information über die Folgen */
    confirm: z.literal(true, { error: 'Bitte bestätigen Sie, dass Sie die Folgen gelesen haben' }),
})
    .strict();
// ---------------------------------------------------------------------------
// Zusatzbenutzer
// ---------------------------------------------------------------------------
export const bookSeatsInput = z
    .object({
    quantity: z.number('Bitte die Anzahl angeben').int('Bitte eine ganze Zahl angeben').min(1, 'Bitte mindestens einen Benutzer buchen').max(EXTRA_USER_MAX_PER_BOOKING, `Bitte höchstens ${EXTRA_USER_MAX_PER_BOOKING} Benutzer auf einmal buchen`),
    /** Bestätigung, dass der Kunde Preis und Kündigungsmöglichkeit gelesen hat */
    confirm: z.literal(true, { error: 'Bitte bestätigen Sie die Buchung' }),
})
    .strict();
/** active: läuft, cancelling: gekündigt zum Ende des bezahlten Monats, ended: beendet */
export const SEAT_STATUSES = ['active', 'cancelling', 'ended'];
// ---------------------------------------------------------------------------
// Zyklus der Zusatzbuchungen
// ---------------------------------------------------------------------------
/**
 * Zusatzbuchungen laufen im Zyklus des Pakets. Zahlt ein Kunde sein Paket ab dem 16., beginnen die Monatsschritte der Zusätze immer am 16. und enden am 15. des Folgemonats.
 * Der Zyklus beginnt an dem Tag, an dem der laufende Abrechnungszeitraum des Pakets begonnen hat. Die Grenzen sind `addMonthsIso(Zyklusbeginn, k)`, immer vom Beginn aus
 * gerechnet, damit ein 31. nicht über die Monate wandert.
 */
export function cycleAnchorOf(periodEnd) {
    return addMonthsIso(addDays(periodEnd, 1), -BILLING_PERIOD_MONTHS);
}
/** Der Monatsschritt des Zyklus, der am Tag `anchor` beginnt, in dem `date` liegt */
export function monthStepOf(anchor, date) {
    const [ay, am] = anchor.split('-').map(Number);
    const [dy, dm] = date.split('-').map(Number);
    let k = Math.max(0, (dy - ay) * 12 + (dm - am));
    while (k > 0 && addMonthsIso(anchor, k) > date)
        k -= 1;
    while (addMonthsIso(anchor, k + 1) <= date)
        k += 1;
    const start = addMonthsIso(anchor, k);
    const nextStart = addMonthsIso(anchor, k + 1);
    return { start, nextStart, end: addDays(nextStart, -1), days: daysBetween(date, nextStart), totalDays: daysBetween(start, nextStart) };
}
/** Der nächste Zyklustag nach `date` */
export function nextCycleDay(anchor, date) {
    return monthStepOf(anchor, date).nextStart;
}
/** Anteiliger Betrag eines angebrochenen Zeitraums: Monatsbetrag mal Tage durch Tage des Monatsschritts, auf den Cent gerundet */
export function prorate(monthlyCents, days, totalDays) {
    return Math.round((monthlyCents * days) / totalDays);
}
/** Arten von Zusatzbuchungen. Alle laufen gleich ab: im Zyklus des Pakets, monatlich, einzeln kündbar. Was eine Buchung bewirkt, steht im Server (AddonService). */
export const ADDON_KINDS = ['seats', 'module'];
export const ADDON_KIND_LABELS = { seats: 'Zusatzbenutzer', module: 'Zusatzmodul' };
/** Einheit je Buchung auf der Rechnung */
export const ADDON_KIND_UNITS = { seats: 'Benutzer', module: 'Modul' };
/**
 * Monatspreis brutto je Zusatzmodul in Cent. Noch ist keines im Backoffice buchbar, die Tabelle ist leer. Mit dem ersten Preis wird das Modul im Server buchbar,
 * die Abrechnung braucht keine Änderung.
 */
export const ADDON_MODULE_PRICES_GROSS_MONTH_CENTS = {};
//# sourceMappingURL=billing.js.map